import {
  Box,
  Button,
  FormControl,
  Grid,
  Input,
  MenuItem,
  Select,
  TableContainer,
  Typography,
  makeStyles,
} from '@material-ui/core';
import _isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import { useDispatch } from 'react-redux';
import ZeroState from '../common/ZeroState';
import FileIcon from '../../assets/FileIcon';
import NoUser from '../../assets/NoUserIcon';
import { ErrorToast, notifySuccess } from '../utils/Utils';
import service from '../../apis/service';
import { apiEndpoints } from '../../constants/apiEndpoints';
import PxDealerColumns from '../report/PxDealership';
import { showProgress } from '../../redux/actions/DealershipActions';

const useStyles = makeStyles(() => ({
  textfield: {
    height: '40px',
    width: '100%',
    padding: '12px 12px 12px 12px',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '14px',
    lineHeight: '21px',
    border: '1px solid gray',
  },
}));

export const TableWrapper = ({
  data,
  tabId,
  batchSize,
  dealerId,
  selectedDep,
  setSelectedDep,
  setAllDepIds,
  selectedTimeRange,
  timePeriod,
  empType,
  Jtype,
  selectedRole,
  setSelectedRole,
  setEmpHash,
  department,
  setDepartment,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);
  const rowVirtualizerInstanceRef = useRef(null);

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [loadedBatch, setLoadedBatch] = useState(1);
  const [roles, setRoles] = useState([]);

  const batchedData = useMemo(() => data?.slice(0, batchSize * loadedBatch), [data, batchSize, loadedBatch]);

  const totalFetched = batchedData?.length;

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 400 && totalFetched < data.list?.length) {
          setLoadedBatch((prevBatch) => prevBatch + 1);
        }
      }
    },
    [totalFetched, data],
  );

  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      console.error(error);
    }
  }, [sorting, columnFilters, globalFilter]);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  useEffect(() => {
    setSorting([]);
  }, [tabId]);

  const getDepartment = async () => {
    dispatch(showProgress(true));
    try {
      const dep = await service.get(apiEndpoints.DEALER_DEPARTMENTS.replace('ID', dealerId));
      setDepartment(dep);
      const allIds = dep?.map((d) => d.id);
      setAllDepIds(allIds);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  useState(() => {
    getDepartment();
  }, []);

  useEffect(() => {
    const dep = department.filter((d) => d.id === selectedDep);
    if (dep.length > 0) {
      setRoles(dep[0].roles);
    } else {
      setRoles([]);
    }

    setSelectedRole(0);
  }, [selectedDep, department]);

  const handleEmailUsageReport = async () => {
    dispatch(showProgress(true));
    const filters = JSON.stringify({
      filters: {
        dealerships: [dealerId],
        trendRequired: false,
        time: {
          ...selectedTimeRange,
          type: timePeriod + 1,
        },
      },
    });
    try {
      await service.post(apiEndpoints.PX_EMAIL, filters);
      notifySuccess('Usage Report Email Sent Successfully');
      dispatch(showProgress(false));
    } catch (err) {
      ErrorToast(err);
      dispatch(showProgress(false));
    }
  };

  const handleEmailPxReport = async () => {
    dispatch(showProgress(true));
    const filters = JSON.stringify({
      filters: {
        dealerships: [dealerId],
        trendRequired: false,
        time: {
          ...selectedTimeRange,
          type: timePeriod + 1,
        },
      },
    });
    try {
      await service.post(apiEndpoints.PX_REPORT_EMAIL, filters);
      notifySuccess('PX Report Email Sent Successfully');
      dispatch(showProgress(false));
    } catch (err) {
      ErrorToast(err);
      dispatch(showProgress(false));
    }
  };

  const getZeroStateScreen = (tab) => {
    if (tab === 0 || tab === 1) {
      return (
        <ZeroState
          Icon={NoUser}
          text="No user found for the applied filters. Please change the filters and try again."
          style={{ paddingBottom: '50px', marginTop: '50px' }}
        />
      );
    }
    return (
      <ZeroState
        Icon={FileIcon}
        text="No data found for the applied filters. Please change the filters and try again."
        style={{ paddingBottom: '50px', marginTop: '50px' }}
      />
    );
  };

  return (
    <TableContainer style={{ maxWidth: '80vw' }}>
      {!_isEmpty(data) ? (
        <MaterialReactTable
          columns={PxDealerColumns(tabId, empType, setEmpHash, Jtype)}
          data={data}
          enableColumnActions
          enableColumnFilters
          enablePagination={false}
          enableSorting
          enableBottomToolbar={false}
          enableTopToolbar
          enableFullScreenToggle
          muiTableBodyRowProps={{ hover: true }}
          enableStickyHeader
          enableMultiRowSelection={false}
          rowCount={data.length}
          enablePinning
          enableMultiSort
          enableColumnResizing
          enableDensityToggle={false}
          initialState={{ density: 'compact' }}
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
            },
          }}
          muiTableContainerProps={{
            ref: tableContainerRef,
            sx: { maxHeight: '30vh', marginBottom: '0px' },
            onScroll: (event) => fetchMoreOnBottomReached(event.target),
          }}
          onColumnFiltersChange={setColumnFilters}
          onGlobalFilterChange={setGlobalFilter}
          onSortingChange={setSorting}
          state={{
            columnFilters,
            globalFilter,
            isLoading: false,
            showProgressBars: false,
            sorting,
          }}
          rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
          rowVirtualizerOptions={{ overscan: 4 }}
          renderTopToolbarCustomActions={() => (
            <Box style={{ display: 'flex', width: '100%', gap: '10px', alignItems: 'center' }}>
              <Grid style={{ width: '200px' }}>
                <FormControl>
                  <Select
                    input={<Input disableUnderline className={classes.textfield} />}
                    value={selectedDep}
                    onChange={(e) => setSelectedDep(e.target.value)}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 4.5 + 8,
                          width: 280,
                        },
                      },
                    }}
                  >
                    {department.map((d) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <MenuItem value={d.id} key={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {tabId !== 1 && (
                <Grid style={{ width: '200px' }}>
                  <FormControl>
                    <Select
                      input={<Input disableUnderline className={classes.textfield} />}
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 48 * 4.5 + 8,
                            width: 350,
                          },
                        },
                      }}
                    >
                      {roles.map((r) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <MenuItem value={r.id} key={r.id}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {tabId === 1 && (
                <Button onClick={handleEmailPxReport}>
                  <Typography color="primary" style={{ borderBottom: '1px solid #622FEE' }} variant="text">
                    Email PX Report
                  </Typography>
                </Button>
              )}
              {tabId !== 2 && (
                <Button onClick={handleEmailUsageReport}>
                  <Typography color="primary" style={{ borderBottom: '1px solid #622FEE' }} variant="text">
                    Email Usage Report
                  </Typography>
                </Button>
              )}
            </Box>
          )}
        />
      ) : (
        getZeroStateScreen(tabId)
      )}
    </TableContainer>
  );
};
